import { useMutation } from "@apollo/client";
import {
  ContentState,
  previewArticle,
  previewContent,
  updateContentList,
} from "@platformx/authoring-state";
import {
  MAPPING,
  SCHEDULE_STATUS,
  ShowToastError,
  ShowToastSuccess,
  capitalizeFirstLetter,
  convertToLowerCase,
  formatPageUrl,
  frameDocumentType,
  getContentUrlPath,
  getCurrentLang,
  getDefaultBrandId,
  getSelectedBrandName,
  getSelectedSite,
  getStoredBrandDetails,
  getSubDomain,
  isFixedContentType,
  isScheduledInFuture,
  makeCreateContentPath,
  useUserSession,
} from "@platformx/utilities";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { createSearchParams } from "react-router-dom";
import brandsApi from "../../services/brands/brands.api";
import contentTypeSchemaApi from "../../services/contentTypeSchema/contentTypeSchema.api";
import contentTypeAPIs, {
  createContentType,
  deleteContentType,
  publishContentType,
} from "../../services/contentTypes/contentTypes.api";
import { LanguageList } from "../../utils/constants";
import { CONTENT_CONSTANTS } from "./Uitls/Constants";
import { mapDeleteContent, mapDuplicateContent, mapUnPublishContent } from "./mapper";

const {
  LANG,
  DRAFT,
  VOD,
  EVENT,
  POLL,
  PUBLISHED,
  QUESTION,
  QUIZ,
  PUBLISH,
  UNPUBLISHED,
  PREVIEW_PATH,
  ARTICLE,
} = CONTENT_CONSTANTS;
const useContentActions = (filter = "ALL", isShared = false) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { contentList, startIndex } = useSelector((state: ContentState) => state);
  const navigate = useNavigate();
  const [getSession] = useUserSession();
  const { userInfo } = getSession();
  const username = `${userInfo.first_name} ${userInfo.last_name}`;
  const [loading, setLoading] = useState(false);
  const [deleteMutate] = useMutation(deleteContentType);
  const [unPublishMutate] = useMutation(publishContentType);
  const [createMutate] = useMutation(createContentType, {
    context: {
      skipBrandNameHeader: isShared,
      headers: {
        language: localStorage.getItem(LANG),
        sitename: getSelectedSite(),
      },
    },
  });

  const location = useLocation();

  const fetchContentDetails = async (listItemDetails: {
    pageName?: string;
    tagName: string | undefined;
    tag_name: string;
    page: any;
  }) => {
    try {
      //we need to check and change
      const response: any = await contentTypeAPIs.fetchContent({
        contentType: listItemDetails.tag_name,
        path: listItemDetails?.page,
        isShared,
      });
      if (response.authoring_getCmsContentByPath) {
        const { authoring_getCmsContentByPath: content } = response;
        return content;
      }
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
    }
  };

  const deleteContent = async (listItemDetails: any, filterObject: any, schemaType: string) => {
    const { filter: filterData = "ALL" } = filterObject;
    setLoading(true);
    try {
      if (
        listItemDetails.page_state === PUBLISHED ||
        listItemDetails.Page_State === PUBLISHED ||
        listItemDetails.Page_State === PUBLISH
      ) {
        await unPublish(listItemDetails, schemaType);
      }
      try {
        const contentToSend = mapDeleteContent(
          listItemDetails.tag_name,
          listItemDetails,
          schemaType,
        );
        const response: any = await deleteMutate({
          variables: {
            ...contentToSend,
          },
        });
        if (response) {
          const searchResponse = await contentTypeAPIs.fetchSearchContent(
            listItemDetails.tag_name,
            location,
            filterData,
            startIndex,
            contentList,
            true,
            { schemaType, isShared },
          );
          dispatch(updateContentList(searchResponse));
          ShowToastSuccess(
            `${capitalizeFirstLetter(listItemDetails.tagName)} ${t("deleted_toast")}`,
          );
        }
      } catch (err: any) {
        const statusCode = err?.networkError?.statusCode || 0;
        ShowToastError(t("api_error_toast"), statusCode);
      }
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
      console.error("error in delete content", err);
    } finally {
      setLoading(false);
    }
  };

  const unPublish = async (listItemDetails: any, schemaType: string) => {
    if (listItemDetails && Object.keys(listItemDetails).length > 0) {
      try {
        if (isFixedContentType(listItemDetails?.tag_name)) {
          const contentToSend = mapUnPublishContent(
            listItemDetails.tag_name,
            listItemDetails.page || listItemDetails?.Page,
          );
          const unPublishResponse = await unPublishMutate({
            variables: {
              ...contentToSend,
            },
          });
          if (unPublishResponse) {
            const response = await contentTypeAPIs.fetchSearchContent(
              listItemDetails.tag_name,
              location,
              filter,
              startIndex,
              contentList,
              true,
              { isShared },
            );
            dispatch(updateContentList(response));
            ShowToastSuccess(
              `${capitalizeFirstLetter(listItemDetails.tagName)} ${t("unpublished_toast")}`,
            );
          }
        } else {
          const contentToSend = {
            document_name: listItemDetails.page,
            document_id: listItemDetails.documentId,
            status: SCHEDULE_STATUS.UNPUBLISH,
          };
          const unPublishResponse: any = await contentTypeSchemaApi.publishContentType({
            contentType: listItemDetails.tag_name,
            input: contentToSend,
            schema_document_type: frameDocumentType(schemaType),
          });
          if (unPublishResponse) {
            const response = await contentTypeAPIs.fetchSearchContent(
              listItemDetails.tag_name,
              location,
              filter,
              startIndex,
              contentList,
              true,
              { schemaType, isShared },
            );
            dispatch(updateContentList(response));
            ShowToastSuccess(
              `${capitalizeFirstLetter(listItemDetails.tagName)} ${t("unpublished_toast")}`,
            );
          }
        }
      } catch (error: any) {
        const statusCode = error?.networkError?.statusCode || 0;
        ShowToastError(error?.graphQLErrors[0]?.message || t("api_error_toast"), statusCode);
      }
    }
  };

  const view = (listItemDetails) => {
    const selectedBrandRoute = formatPageUrl(getSelectedBrandName() || i18n.language);

    // Courses use a dedicated query-param URL and are not slug-based.
    if (listItemDetails.tagName === convertToLowerCase("Courses")) {
      window.open(
        `${getSubDomain()}/${selectedBrandRoute}/course/course-details?courseId=${
          listItemDetails?.course_id
        }`,
      );
      return;
    }

    // Slug-first URL — falls back to the legacy navigation_slug (Player
    // Profile/dynamic types) and finally the doc-path inside getContentUrlPath.
    window.open(`${getSubDomain()}/${selectedBrandRoute}${getContentUrlPath(listItemDetails)}`);
  };

  const edit = (listItemDetails: { tagName: string; page: any; category: any }) => {
    dispatch(previewContent({}));
    dispatch(previewArticle({}));
    const navigateTo = makeCreateContentPath(listItemDetails.tagName, listItemDetails?.category);
    navigate(`${navigateTo}?path=${listItemDetails.page}`, {
      state: listItemDetails.tagName?.toLowerCase(),
    });
  };

  const editDynamicContent = (listItemDetails: {
    tagName: string;
    page: any;
    tag_name: string;
    category: string;
    subCategory: string;
  }) => {
    dispatch(previewContent({}));
    navigate(
      `/update/${listItemDetails?.category}/${listItemDetails?.subCategory}/${listItemDetails?.page}`,
    );
  };

  const editPage = (listItemDetails: { path: any }) => {
    localStorage.setItem("path", listItemDetails.path.toString());
    navigate({
      pathname: "/edit-page",
      search: `?${createSearchParams({
        page: listItemDetails.path.toString(),
      })}`,
    });
  };

  const preview = async (listItemDetails: any) => {
    const selectedItem = await fetchContentDetails(listItemDetails);
    const type = listItemDetails?.tag_name;
    if (selectedItem && Object.keys(selectedItem).length > 0) {
      try {
        if (
          selectedItem?.page_state === DRAFT ||
          selectedItem?.page_state === UNPUBLISHED ||
          selectedItem?.Page_State === DRAFT ||
          selectedItem?.Page_State === UNPUBLISHED
        ) {
          if (
            selectedItem?.questions?.length &&
            capitalizeFirstLetter(listItemDetails.tagName) === QUIZ
          ) {
            const questionPromise = selectedItem?.questions?.map(async (qus: any) => {
              const question = await fetchContentDetails({
                tagName: QUESTION,
                page: qus,
                tag_name: listItemDetails.tag_name,
              });
              if (question?.background_content) {
                return question;
              }
            });
            Promise.all(questionPromise).then(function (results) {
              dispatch(previewContent({ ...selectedItem, contentType: type, questions: results }));
              navigate(PREVIEW_PATH, {
                state: { contentType: QUIZ, pageName: selectedItem.page },
              });
            });
          } else if (capitalizeFirstLetter(listItemDetails.tagName) === POLL) {
            dispatch(previewContent({ ...selectedItem, contentType: type }));
            navigate(PREVIEW_PATH, {
              state: { contentType: POLL, pageName: selectedItem.page },
            });
          } else if (capitalizeFirstLetter(listItemDetails.tagName) === ARTICLE) {
            dispatch(
              previewContent({
                ...selectedItem,
                page_lastmodifiedby: selectedItem.last_modifiedBy,
                developed_date: selectedItem.creationDate,
                contentType: ARTICLE,
              }),
            );
            navigate(PREVIEW_PATH, {
              state: { contentType: ARTICLE, pageName: selectedItem.page },
            });
          } else if (capitalizeFirstLetter(listItemDetails.tagName) === EVENT) {
            const eventToPreview = {
              ...selectedItem,
              settings: selectedItem?.settingsProperties,
              PageTags: selectedItem?.tags,
              lastModifiedDate: selectedItem?.modificationDate,
              last_modification_date: selectedItem?.modificationDate,
              AnalyticsEnable: selectedItem?.analytics_enable,
            };
            dispatch(previewContent({ ...eventToPreview, contentType: type }));
            navigate(PREVIEW_PATH, {
              state: { contentType: MAPPING.Event, pageName: selectedItem.page },
            });
          } else if (capitalizeFirstLetter(listItemDetails.tagName) === VOD) {
            dispatch(
              previewContent({
                ...selectedItem,
                page_lastmodifiedby: selectedItem.last_modifiedBy,
                developed_date: selectedItem.creationDate,
                contentType: type,
              }),
            );
            navigate(PREVIEW_PATH, {
              state: { contentType: MAPPING.Vod, pageName: selectedItem.page },
            });
          } else {
            ShowToastError(t(PREVIEW_PATH));
          }
        }
      } catch (error: any) {
        const statusCode = error?.networkError?.statusCode || 0;
        ShowToastError(error?.graphQLErrors[0]?.message || t("api_error_toast"), statusCode);
      }
    }
  };

  const calculateInitialValues = (fields) => {
    const initialValues: any = {};
    if (fields?.length > 0) {
      fields.forEach((field) => {
        initialValues[field.name] = field?.value || (field?.type === "boolean" ? false : "");
      });
    }
    return initialValues;
  };

  const calculateInitialValuesWithData = (initialData, data) => {
    const initialValues: any = {};
    const dataObj = Object.keys(initialData);
    dataObj.forEach((field) => {
      initialValues[field] = data[field];
    });
    return initialValues;
  };

  const fetchSchema = async (contentType: string) => {
    try {
      const response: any = await contentTypeSchemaApi.getSchemaDynamic({
        filter: contentType,
      });
      if (response.authoring_getSchema) {
        const { authoring_getSchema: content } = response;
        return content;
      }
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
    }
  };

  const fetchSchemaContentDetails = async ({ contentType, documentId, schemaType }) => {
    try {
      const response: any = await contentTypeSchemaApi.getSchemaDynamicWithData({
        contentType: contentType,
        name: documentId,
        schema_document_type: schemaType === "collection" ? schemaType : "contentType",
      });
      if (response.authoring_getSchemaContent) {
        const { authoring_getSchemaContent: content } = response;
        return content;
      }
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
    }
  };

  const createSchemaContent = async (contentInstance, contentType, schemaType, title) => {
    try {
      const formatUrl = formatPageUrl(title);
      const newContent = {
        ...contentInstance,
        title: title,
        name: formatUrl,
      };
      const response: any = await contentTypeSchemaApi.saveContent(
        {
          contentType: contentType,
          input: newContent,
          schema_document_type: schemaType === "collection" ? schemaType : "contentType",
        },
        isShared,
      );
      const { authoring_saveSchemaContent: content } = response;
      return content;
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
    }
  };

  const duplicate = async (
    IsDuplicate,
    title: any,
    language: string | string[],
    listItemDetails: any,
    schemaType?: string,
    title_display = "", //added title_display for dynamic content duplicate
  ) => {
    if (isFixedContentType(listItemDetails?.tag_name)) {
      try {
        setLoading(true);
        const selectedItem = await fetchContentDetails(listItemDetails);
        if (selectedItem && Object.keys(selectedItem).length > 0) {
          const selectedBrandRoute = formatPageUrl(getSelectedBrandName() || i18n.language);
          const contentToSend = mapDuplicateContent(
            listItemDetails.tag_name,
            title,
            IsDuplicate || false,
            selectedItem,
            username,
            selectedBrandRoute,
          );
          const selectedLanguage = LanguageList.filter((langObj) =>
            language.includes(langObj.value),
          );
          const promises = selectedLanguage.map(async (lang) => {
            return await createMutate({
              variables: {
                contenttype: listItemDetails.tag_name,
                input: { ...contentToSend },
              },
              context: {
                skipBrandNameHeader: isShared,
                headers: {
                  language: lang.id,
                  sitename: getSelectedSite(),
                },
              },
            });
          });

          const response = await Promise.all(promises);

          if (response && response.length > 0) {
            const searchResponse: any = await contentTypeAPIs.fetchSearchContent(
              listItemDetails.tag_name,
              location,
              filter,
              startIndex,
              contentList,
              true,
              { isShared },
            );
            dispatch(updateContentList(searchResponse));
            for (const lang of selectedLanguage) {
              ShowToastSuccess(
                `${t(capitalizeFirstLetter(listItemDetails.tagName))} ${t("duplicated_toast")} ${t(
                  "for",
                )} ${lang.value}`,
              );
            }
          }
        }
      } catch (error: any) {
        const statusCode = error?.networkError?.statusCode || 0;
        ShowToastError(
          error?.graphQLErrors?.[0]
            ? `${error.graphQLErrors[0].message} ${t("for")} ` //${l.value}
            : t("api_error_toast"),
          statusCode,
        );
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        const schema = await fetchSchema(listItemDetails.tag_name);
        if (schema?.fields?.length > 0) {
          const initialValues = await calculateInitialValues(schema?.fields);
          const schemaContent = await fetchSchemaContentDetails({
            contentType: listItemDetails.tag_name,
            documentId: listItemDetails.page,
            schemaType: frameDocumentType(schemaType),
            // schemaType.toLowerCase() === "content" || schemaType.toLowerCase() === "contenttype"
            //   ? "contentType"
            //   : "collection",
          });
          const finalschema = calculateInitialValuesWithData(initialValues, schemaContent);
          if (finalschema && Object.keys(finalschema).length > 0) {
            const createResponse: any = await createSchemaContent(
              finalschema,
              listItemDetails.tag_name,
              schemaType,
              title,
            );
            if (createResponse?.id) {
              const response: any = await contentTypeAPIs.fetchSearchContent(
                listItemDetails.tag_name,
                location,
                filter,
                startIndex,
                contentList,
                true,
                { schemaType, isShared },
              );
              dispatch(updateContentList(response));
              ShowToastSuccess(
                `${title_display || t(capitalizeFirstLetter(listItemDetails.tagName))} ${t("duplicated_toast")}`,
              );
            }
          }
        }
      } catch (error: any) {
        const statusCode = error?.networkError?.statusCode || 0;
        ShowToastError(
          error?.graphQLErrors?.[0]
            ? `${error.graphQLErrors[0].message} ${t("for")} ` //${l.value}
            : t("api_error_toast"),
          statusCode,
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const duplicateToSite = async (IsDuplicate, title: any, listItemDetails: any, siteTitle: any) => {
    try {
      const selectedItem = await fetchContentDetails(listItemDetails);

      if (selectedItem && Object.keys(selectedItem).length > 0) {
        const selectedBrandRoute = formatPageUrl(getSelectedBrandName() || i18n.language);
        const contentToSend = mapDuplicateContent(
          listItemDetails.tag_name,
          title,
          IsDuplicate || false,
          selectedItem,
          username,
          selectedBrandRoute,
        );
        const result = await createMutate({
          variables: {
            contenttype: listItemDetails.tag_name,
            input: { ...contentToSend },
          },
          context: {
            skipBrandNameHeader: isShared,
            headers: {
              language: getCurrentLang(),
              sitename: siteTitle,
            },
          },
        });
        return result;
      }
    } catch (error: any) {
      const statusCode = error?.networkError?.statusCode || 0;

      ShowToastError(
        error.graphQLErrors[0]
          ? `${error.graphQLErrors[0].message} ${t("for")} ` //${l.value}
          : t("api_error_toast"),
        statusCode,
      );
    }
  };

  const shareContentWithBrands = async (
    listItemDetails: any,
    primaryBrandId: string,
    selectedBrandIds: string[],
    schemaDocumentType?: string,
    prefetchedDoc?: any,
  ) => {
    try {
      setLoading(true);
      const defaultBrandId =
        primaryBrandId ||
        getDefaultBrandId(listItemDetails?.brand_name) ||
        getStoredBrandDetails()[0]?.brandId ||
        "";
      const result = await brandsApi.shareWithBrands({
        path: listItemDetails?.page || listItemDetails?.path,
        contentType: listItemDetails?.tag_name || listItemDetails?.tagName,
        defaultBrandId,
        selectedBrandIds,
        schemaDocumentType,
        pageState: listItemDetails?.page_state || listItemDetails?.status,
        prefetchedDoc,
        // A live item (published, or published with newer draft changes) is published again so the
        // new brand takes effect on the site — same as the editor's Publish. An item with a pending
        // (future) publish/unpublish schedule is left alone: a plain re-publish could drop or
        // override that schedule, which the schedule menu (KIWI-6576) manages.
        publishAfter:
          (listItemDetails?.is_published === true ||
            String(listItemDetails?.page_state || listItemDetails?.status).toLowerCase() ===
              "published") &&
          !isScheduledInFuture(listItemDetails?.scheduledPublishTriggerDateTime) &&
          !isScheduledInFuture(listItemDetails?.scheduledUnPublishTriggerDateTime),
      });
      ShowToastSuccess(`${listItemDetails?.title} ${t("shared_toast")}`);
      if (result?.publishError) {
        // The share itself is saved (as a draft revision); only the re-publish failed.
        ShowToastError(`${t("api_error_toast")}: ${result.publishError}`);
      }
      return result;
    } catch (error: any) {
      const statusCode = error?.networkError?.statusCode || 0;
      ShowToastError(
        error?.graphQLErrors?.[0] ? error.graphQLErrors[0].message : t("api_error_toast"),
        statusCode,
      );
      return undefined;
    } finally {
      setLoading(false);
    }
  };
  return {
    shareContentWithBrands,
    fetchSchemaContentDetails,
    unPublish,
    duplicate,
    preview,
    view,
    deleteContent,
    edit,
    editDynamicContent,
    editPage,
    fetchContentDetails,
    duplicateToSite,
    loading,
  };
};

export default useContentActions;
